import type { MappedEntityType, MarkerSetupType, UploadedFileStepType } from '@core/core/types'
import { isDefined } from 'common/helpers'
import { useEffect, useState } from 'react'

import { getSeparatorString, parseHeaderLines, parseTable, parseTableRow } from './helper'
import { parseMarkersTable } from './parsers'
import type { MarkersParsedData } from './types'

export const useMarkerParser = (
  file: Blob | undefined,
  activeStep: UploadedFileStepType,
  settings?: MarkerSetupType,
  mappings?: MappedEntityType[],
): MarkersParsedData => {
  const [parseResult, setParseResult] = useState<MarkersParsedData>({
    fileHeader: undefined,
  })

  const encoding = settings?.encoding
  useEffect(() => {
    if (file && encoding) {
      parseHeaderLines(file, encoding).then(lines => {
        setParseResult(prevState => {
          const prevHeader = prevState.fileHeader
          const fileHeader = prevHeader ? { ...prevHeader, lines } : { firstLineCells: [], lines }
          return { ...prevState, fileHeader }
        })
      })
    }
  }, [file, encoding])

  const separator = settings?.separator
  const firstLine = isDefined(settings?.missedLinesCount)
    ? parseResult.fileHeader?.lines[settings.missedLinesCount]
    : undefined
  useEffect(() => {
    setParseResult(prevState => {
      const prevHeader = prevState.fileHeader
      if (!prevHeader || !separator) return prevState
      const firstLineCells = firstLine
        ? parseTableRow(firstLine, getSeparatorString(separator))
        : []
      const fileHeader = { ...prevHeader, firstLineCells }
      return { ...prevState, fileHeader }
    })
  }, [firstLine, separator])

  const isInSetup = activeStep === 'setup'
  useEffect(() => {
    if (isInSetup && parseResult.dataTable) {
      setParseResult(prevState => ({
        ...prevState,
        dataTable: undefined,
        result: undefined,
      }))
    }
  }, [isInSetup])

  const isInMapping = activeStep === 'mapping'
  useEffect(() => {
    if (file && isInMapping && settings) {
      parseTable(file, settings).then(dataTable => {
        setParseResult(prevState => ({
          ...prevState,
          dataTable,
        }))
      })
    }
  }, [file, isInMapping, settings])

  const isLoading = activeStep === 'loading'
  const dataTable = parseResult.dataTable
  useEffect(() => {
    if (isLoading && dataTable && mappings && settings) {
      const result = parseMarkersTable(dataTable, settings, mappings)
      setParseResult(prevState => ({
        ...prevState,
        result,
      }))
    }
  }, [isLoading, dataTable, settings, mappings])

  return parseResult
}
